'use client';
import React, { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { ExternalLink, Music2, Headphones, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface Release {
  id: string;
  type: string;
  title: string;
  artist: string;
  featuredArtists: string | null;
  year: string;
  genre: string | null;
  cover: string;
  coverAlt: string;
  streams: string;
  isFeatured: boolean;
  links: {
    apple: string;
    spotify: string;
    youtube: string;
    soundcloud: string;
    audiomack: string;
  };
}

const streamingPlatforms = [
  { name: 'Apple Music', href: 'https://music.apple.com/us/artist/hadatha/1611554470', icon: '♪' },
  { name: 'SoundCloud', href: 'https://soundcloud.com/johnbruddatz', icon: '◉' },
  { name: 'YouTube', href: 'https://www.youtube.com/watch?v=JsSVXOFFypU', icon: '▶' },
];

export default function FeaturedReleases() {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/music')
      .then((r) => r.json())
      .then((data) => setReleases(data.releases ?? []))
      .catch(() => setReleases([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = releases.find((r) => r.isFeatured) ?? releases[0];
  const rest = releases.filter((r) => r.id !== featured?.id).slice(0, 5);

  if (loading) {
    return (
      <section id="music" className="py-24 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (!featured) {
    return (
      <section id="music" className="py-24 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Discography</p>
              <h2 className="text-4xl font-800 text-foreground">Latest Releases</h2>
            </div>
          </div>
          <div className="text-center py-16 text-muted-foreground">
            <Music2 size={40} className="mx-auto mb-4 opacity-40" />
            <p>No releases available yet.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="music" className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Discography</p>
            <h2 className="text-4xl font-800 text-foreground">Latest Releases</h2>
          </div>
          <Link href="/music" className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
            View All Releases <ExternalLink size={14} />
          </Link>
        </div>

        {/* Featured + Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Featured release */}
          <div className="lg:col-span-5 group">
            <div className="relative rounded-2xl overflow-hidden bg-card border border-border card-glow card-glow-hover transition-all duration-300">
              <div className="relative aspect-square">
                <AppImage
                  src={featured.cover}
                  alt={featured.coverAlt}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-700"
                  sizes="(max-width: 1024px) 100vw, 40vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-2.5 py-1 text-[10px] font-700 tracking-widest uppercase rounded-full bg-primary/90 text-primary-foreground">
                    {featured.type} · {featured.year}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-xs text-white/60 mb-1">{featured.artist}</p>
                  <h3 className="text-3xl font-800 text-white mb-1">{featured.title}</h3>
                  <div className="flex items-center gap-2">
                    {featured.genre && <span className="text-xs text-white/50">{featured.genre}</span>}
                    {featured.streams && featured.streams !== '0' && (
                      <>
                        <span className="text-white/30">·</span>
                        <Headphones size={12} className="text-white/50" />
                        <span className="text-xs text-white/50 font-tabular">{featured.streams} streams</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {/* Streaming links */}
              <div className="p-4 grid grid-cols-3 gap-2">
                {streamingPlatforms.map((p) => (
                  <a
                    key={`streaming-${p.name}`}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 hover:bg-muted/70 text-xs font-500 text-muted-foreground hover:text-foreground transition-all active:scale-95"
                  >
                    <span className="text-base" style={{ lineHeight: 1 }}>{p.icon}</span>
                    {p.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Release grid */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {rest.map((release) => (
              <Link key={release.id} href="/music" className="group relative rounded-xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-200 cursor-pointer">
                <div className="relative aspect-square">
                  <AppImage
                    src={release.cover}
                    alt={release.coverAlt}
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-500"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center">
                      <Music2 size={20} className="text-primary-foreground" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <span className="text-[9px] font-600 tracking-widest text-primary uppercase">{release.type} · {release.year}</span>
                  <p className="text-sm font-700 text-foreground mt-0.5 truncate">{release.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{release.artist}</p>
                  {release.streams && release.streams !== '0' && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Headphones size={10} className="text-muted-foreground" />
                      <span className="text-[10px] font-mono text-muted-foreground font-tabular">{release.streams}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Streaming platforms bar */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 py-6 border-t border-border">
          <span className="text-xs text-muted-foreground mr-2">Stream on:</span>
          {streamingPlatforms.map((p) => (
            <a
              key={`platform-bar-${p.name}`}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-500 text-muted-foreground hover:text-primary border border-border hover:border-primary/30 rounded-lg transition-all"
            >
              {p.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}